import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import {
  LayoutDashboard, Briefcase, Users, Building2, Calendar,
  BarChart2, FileText, Settings, Shield, Zap, Phone,
  BookOpen, HelpCircle, Upload, Search, User, ChevronRight,
  Loader2, ArrowUpDown
} from 'lucide-react';
import { VOICE_CALLING_ENABLED } from '@/config/features';

// ─── Static navigation destinations ─────────────────────────────────────────
const NAV_PAGES = [
  { label: 'Dashboard',        path: '/app/dashboard',          icon: LayoutDashboard, tag: 'page' },
  { label: 'Jobs',             path: '/app/jobs',               icon: Briefcase,       tag: 'page' },
  { label: 'Candidates',       path: '/app/candidates',         icon: Users,           tag: 'page' },
  { label: 'Clients',          path: '/app/clients',            icon: Building2,       tag: 'page' },
  { label: 'Interviews',       path: '/app/interviews',         icon: Calendar,        tag: 'page' },
  { label: 'Meetings',         path: '/app/meetings',           icon: Calendar,        tag: 'page' },
  { label: 'HiroMatch',        path: '/app/hiromatch',          icon: Users,           tag: 'page' },
  { label: 'Submissions',      path: '/app/submissions',        icon: FileText,        tag: 'page' },
  { label: 'Analytics',        path: '/app/analytics',          icon: BarChart2,       tag: 'page' },
  { label: 'Reports',          path: '/app/reports',            icon: FileText,        tag: 'page' },
  { label: 'Outreach',         path: '/app/outreach',           icon: Zap,             tag: 'page' },
  // AI Calling + AI Autodialer are parked (config/features.js) — no entry point
  // while voice calling is off.
  ...(VOICE_CALLING_ENABLED ? [
    { label: 'AI Calling',       path: '/app/ai-calling',         icon: Phone,           tag: 'page' },
    { label: 'AI Autodialer',    path: '/app/ai-autodialer',      icon: Phone,           tag: 'page' },
  ] : []),
  { label: 'Candidate Data',   path: '/app/bulk-import',        icon: Upload,          tag: 'page' },
  { label: 'Training',         path: '/app/training',           icon: BookOpen,        tag: 'page' },
  { label: 'Security',         path: '/app/security',           icon: Shield,          tag: 'page' },
  { label: 'Settings',         path: '/app/control-settings',   icon: Settings,        tag: 'page' },
  { label: 'Help',             path: '/app/help',               icon: HelpCircle,      tag: 'page' },
  { label: 'AI Recruiter',     path: '/app/ai-recruiter',       icon: Search,          tag: 'page' },
];

const SECTION_LABEL = {
  page:      'Pages',
  candidate: 'Candidates',
  job:       'Jobs',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function highlight(text, query) {
  if (!query || !text) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-tech-blue-500/30 text-tech-blue-300 rounded-sm">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────
export function CommandPalette() {
  const [open, setOpen]               = useState(false);
  const [query, setQuery]             = useState('');
  const [items, setItems]             = useState([]);
  const [activeIdx, setActiveIdx]     = useState(0);
  const [loading, setLoading]         = useState(false);
  const inputRef                      = useRef(null);
  const listRef                       = useRef(null);
  const navigate                      = useNavigate();
  const debounceRef                   = useRef(null);

  // Open with Cmd+K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setActiveIdx(0);
    }
  }, [open]);

  // Build default items (no query)
  const buildDefault = useCallback(() => {
    setItems(NAV_PAGES.map(p => ({ ...p, type: 'page' })));
    setActiveIdx(0);
  }, []);

  // Fetch search results
  const search = useCallback(async (q) => {
    if (!q.trim()) { buildDefault(); return; }
    setLoading(true);
    try {
      const pages = NAV_PAGES
        .filter(p => p.label.toLowerCase().includes(q.toLowerCase()))
        .map(p => ({ ...p, type: 'page' }));

      const [candRes, jobsRes] = await Promise.all([
        api.get('/candidates', { params: { search: q, page_size: 5, pool: 'all' } }),
        api.get('/jobs', { params: { search: q, page_size: 5 } }),
      ]);

      const candidates = (candRes.data.candidates || []).map(c => ({
        type: 'candidate', tag: 'candidate',
        label: c.name || 'Unknown',
        sub: [c.current_title, c.location].filter(v => typeof v === 'string' && v.trim()).join(' · '),
        id: c.id,
        icon: User,
      }));
      const ql = q.toLowerCase();
      const jobs = (jobsRes.data.jobs || jobsRes.data || [])
        .filter(j => (j.title || '').toLowerCase().includes(ql) || (j.client_name || '').toLowerCase().includes(ql))
        .slice(0, 5)
        .map(j => ({
        type: 'job', tag: 'job',
        label: j.title || 'Untitled',
        sub: [j.client_name, j.location].filter(v => typeof v === 'string' && v.trim()).join(' · '),
        id: j.id,
        icon: Briefcase,
      }));

      const next = [...pages, ...candidates, ...jobs];
      setItems(next);
      setActiveIdx(0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [buildDefault]);

  // Debounce search
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!open) return;
    debounceRef.current = setTimeout(() => search(query), 220);
    return () => clearTimeout(debounceRef.current);
  }, [query, open, search]);

  // Init default list when opened
  useEffect(() => { if (open) buildDefault(); }, [open, buildDefault]);

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  const handleSelect = useCallback((item) => {
    if (item.type === 'candidate') navigate(`/app/candidates/${item.id}`);
    else if (item.type === 'job')  navigate(`/app/jobs/${item.id}/pipeline`);
    else                           navigate(item.path);
    setOpen(false);
  }, [navigate]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, items.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && items[activeIdx]) handleSelect(items[activeIdx]);
    if (e.key === 'Escape') setOpen(false);
  };

  // Group items by type for section headers
  const grouped = items.reduce((acc, item, idx) => {
    const key = item.tag;
    if (!acc[key]) acc[key] = [];
    acc[key].push({ ...item, _idx: idx });
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="p-0 gap-0 bg-surface-1 border border-white/[0.08] shadow-2xl shadow-black/60 max-w-xl overflow-hidden rounded-xl"
        data-testid="command-palette"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.06]">
          <Search className="w-4 h-4 text-slate-500 shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search candidates, jobs, or navigate…"
            className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-slate-600"
            data-testid="command-palette-input"
          />
          {loading && <Loader2 className="w-4 h-4 text-slate-600 animate-spin shrink-0" />}
          {!loading && (
            <kbd className="hidden sm:flex items-center gap-1 text-xs text-slate-600 bg-white/[0.04] border border-white/[0.06] rounded px-1 py-1">
              esc
            </kbd>
          )}
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[420px] overflow-y-auto py-2">
          {items.length === 0 && !loading && (
            <div className="text-center py-12 text-slate-600 text-sm">
              {query ? `No results for "${query}"` : 'Start typing to search…'}
            </div>
          )}

          {Object.entries(grouped).map(([tag, group]) => (
            <div key={tag}>
              {/* Section label */}
              <p className="px-4 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-600">
                {SECTION_LABEL[tag] || tag}
              </p>
              {group.map((item) => {
                const Icon = item.icon;
                const isActive = item._idx === activeIdx;
                return (
                  <button
                    key={`${tag}-${item.id || item.path}`}
                    data-idx={item._idx}
                    data-testid={`palette-item-${item._idx}`}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setActiveIdx(item._idx)}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors
                      ${isActive ? 'bg-white/[0.06]' : 'hover:bg-white/[0.03]'}`}
                  >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0
                      ${isActive ? 'bg-tech-blue-500/20' : 'bg-white/[0.04]'}`}>
                      <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-tech-blue-400' : 'text-slate-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${isActive ? 'text-white' : 'text-slate-300'}`}>
                        {highlight(item.label, query)}
                      </p>
                      {item.sub && (
                        <p className="text-xs text-slate-600 truncate mt-1">{item.sub}</p>
                      )}
                    </div>
                    {isActive && <ChevronRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />}
                    {item.tag === 'page' && !isActive && (
                      <Badge className="text-xs px-1 py-0 bg-white/[0.04] text-slate-600 border-0 shrink-0">page</Badge>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer hint */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-white/[0.04] text-xs text-slate-700">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><ArrowUpDown className="w-3 h-3" /> navigate</span>
            <span><kbd className="bg-white/[0.04] px-1 rounded">↵</kbd> open</span>
            <span><kbd className="bg-white/[0.04] px-1 rounded">esc</kbd> close</span>
          </div>
          <span className="flex items-center gap-1">
            <kbd className="bg-white/[0.04] px-1 rounded">⌘K</kbd> to toggle
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
